
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { generateSpeech, transcribeAudio, translateText, getTextCompletion } from './services/gemini-service';
import { decodeBase64, decodePcmToAudioBuffer, audioBufferToWavBlob, audioBufferToMp3Blob } from './utils/audio-utils';
import { Spinner } from './components/Spinner';
import { 
    auth, 
    signInWithGoogle, 
    logout, 
    syncUserProfile, 
    db, 
    saveAudioHistory, 
    saveTranscriptionHistory,
    deleteHistoryItem,
    deductCredit,
    searchUserByEmail,
    addCredits,
    updateUserTier,
    logPaymentInterest
} from './services/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot, collection, query, orderBy, getDoc, limit } from 'firebase/firestore';
import { 
    Play, 
    Pause, 
    Download, 
    FileText, 
    Mic, 
    Clock, 
    Upload, 
    Bookmark, 
    Check, 
    Trash2, 
    ChevronUp, 
    RotateCcw, 
    Clipboard,
    AlertCircle,
    LogOut,
    User as UserIcon,
    Wallet,
    ShieldCheck,
    Search,
    PlusCircle,
    Mail
} from 'lucide-react';
import { cn } from './utils/cn';

const VOICES = [
    { id: 'Kore', name: 'Giana (Female)', desc: 'Sauti changamfu na rafiki, inafaa kwa matangazo na maelezo mbalimbali.' },
    { id: 'Puck', name: 'Austin (Male)', desc: 'Sauti nzito sana ya kiume yenye mamlaka, inafaa kwa simulizi zenye nguvu na uzito wa pekee.' },
    { id: 'Charon', name: 'Gian (Male)', desc: 'Sauti nzito na tulivu, inafaa kwa hadithi za huzuni au simulizi za kina.' },
    { id: 'Zephyr', name: 'Mariam (Female)', desc: 'Sauti laini na yenye kutuliza, inafaa kwa mazoezi ya utulivu (meditation) au maelezo ya ndani.' },
    { id: 'Fenrir', name: 'Daudi (Male)', desc: 'Sauti ya mtangazaji wa kimataifa (BBC style) yenye mamlaka na weledi mkubwa.' },
    { id: 'Child', name: 'Mtoto (Child)', desc: 'Sauti nyepesi ya kitoto, inafaa kwa hadithi za watoto au mafunzo ya shule.' },
];

const TONES = [
    { id: 'standard', name: 'Standard' },
    { id: 'cheerful', name: 'Cheerful' },
    { id: 'formal', name: 'Formal' },
    { id: 'calm', name: 'Calm' },
    { id: 'excited', name: 'Excited' },
];

interface SavedTranscription {
    id: string;
    fileName: string;
    text: string;
    date: string;
    type: 'transcribe';
    title: string;
}

interface SavedSpeech {
    id: string;
    text: string;
    voice: string;
    voiceName: string;
    tone: string;
    toneName: string;
    date: string;
    seed?: number;
    type: 'tts';
    title: string;
}

// Check for API Keys safely
const getApiKey = () => {
    try {
        // AI Studio standardized variable
        const key = process.env.GEMINI_API_KEY || (import.meta as any).env?.VITE_GEMINI_API_KEY;
        return key || null;
    } catch (e) {
        return null;
    }
};

const API_KEY_EXISTS = !!getApiKey();
const OWNER_EMAIL = 'reuben.msaki@gmail.com';

const App: React.FC = () => {
    const [mode, setMode] = useState<'tts' | 'transcribe' | 'history' | 'admin'>('tts');

    // API Key Error State
    // Only show the warning in development mode if the key is missing.
    const [apiKeyError, setApiKeyError] = useState(false);

    useEffect(() => {
        // Perform check after mount to avoid SSR/hydration issues
        const isDev = import.meta.env.DEV;
        if (isDev && !API_KEY_EXISTS) {
            setApiKeyError(true);
        }
    }, []);
    
    // Auth State
    const [user, setUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<any>(null);
    const [isAuthLoading, setIsAuthLoading] = useState(true);

    // TTS State
    const [text, setText] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEncodingMp3, setIsEncodingMp3] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [audioData, setAudioData] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [selectedVoice, setSelectedVoice] = useState<string>(VOICES[0].id);
    const [selectedTone, setSelectedTone] = useState<string>(TONES[0].id);
    const [playbackRate, setPlaybackRate] = useState<number>(1.0);
    const [voiceSeed, setVoiceSeed] = useState<number>(Math.floor(Math.random() * 1000000));
    const [useManualSeed, setUseManualSeed] = useState<boolean>(false);
    const [savedSpeeches, setSavedSpeeches] = useState<SavedSpeech[]>([]);
    const [isSpeechSaved, setIsSpeechSaved] = useState<boolean>(false);
    
    // Transcription State
    const [transcriptionFile, setTranscriptionFile] = useState<File | null>(null);
    const [transcriptionResult, setTranscriptionResult] = useState<string>('');
    const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
    const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
    const [savedTranscriptions, setSavedTranscriptions] = useState<SavedTranscription[]>([]);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [showTopUp, setShowTopUp] = useState<boolean>(false);

    // History Library State
    const [historySearch, setHistorySearch] = useState('');
    const [historyTypeFilter, setHistoryTypeFilter] = useState<'all' | 'tts' | 'transcribe'>('all');

    const audioContextRef = useRef<AudioContext | null>(null);
    const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
    const audioBufferRef = useRef<AudioBuffer | null>(null);

    useEffect(() => {
        if (audioSourceRef.current) {
            audioSourceRef.current.playbackRate.value = playbackRate;
        }
    }, [playbackRate]);

    // Firebase Auth Listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            try {
                setUser(currentUser);
                if (currentUser) {
                    await syncUserProfile(currentUser);
                    // Listen to user profile for credits etc
                    onSnapshot(doc(db, 'users', currentUser.uid), (snapshot) => {
                        setUserProfile(snapshot.data());
                    }, (err) => {
                        console.error("Firestore profile listener error:", err);
                        // Gracefully fallback to an empty profile if quota hit
                        if (err.message.includes('Quota')) {
                           setUserProfile({ tier: 'free', credits: 0 });
                        }
                    });
                } else {
                    setUserProfile(null);
                }
            } catch (err) {
                console.error("Auth sync error:", err);
            } finally {
                setIsAuthLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    // Firebase History Listeners
    useEffect(() => {
        if (!user) {
            setSavedSpeeches([]);
            setSavedTranscriptions([]);
            return;
        }

        const qSpeech = query(collection(db, 'users', user.uid, 'history'), orderBy('createdAt', 'desc'), limit(20));
        const unsubSpeech = onSnapshot(qSpeech, (snapshot) => {
            const speeches = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id,
                date: (doc.data() as any).createdAt?.toDate().toLocaleString() || 'Pending...',
                type: 'tts' as const,
                title: `${(doc.data() as any).voiceId} (${(doc.data() as any).tone})`
            } as SavedSpeech));
            setSavedSpeeches(speeches);
        }, (err) => {
            console.error("Firestore history listener error:", err);
        });

        const qTrans = query(collection(db, 'users', user.uid, 'transcriptions'), orderBy('createdAt', 'desc'), limit(20));
        const unsubTrans = onSnapshot(qTrans, (snapshot) => {
            const trans = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id,
                text: (doc.data() as any).transcribedText || '', // Map transcribedText to text
                date: (doc.data() as any).createdAt?.toDate().toLocaleString() || 'Pending...',
                type: 'transcribe' as const,
                title: 'Transcription'
            } as SavedTranscription));
            setSavedTranscriptions(trans);
        }, (err) => {
            console.error("Firestore transcription listener error:", err);
        });

        return () => {
            unsubSpeech();
            unsubTrans();
        };
    }, [user]);

    // Derived unified history for the Library
    const filteredHistory = useMemo(() => {
        const combined = [...savedSpeeches, ...savedTranscriptions];
        combined.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        return combined.filter(item => {
            const matchesSearch = item.text.toLowerCase().includes(historySearch.toLowerCase()) || 
                                 item.title.toLowerCase().includes(historySearch.toLowerCase());
            const matchesType = historyTypeFilter === 'all' || item.type === historyTypeFilter;
            return matchesSearch && matchesType;
        });
    }, [savedSpeeches, savedTranscriptions, historySearch, historyTypeFilter]);

    // Derived Auth state
    const isOwner = user?.email === OWNER_EMAIL;
    const userTier = useMemo(() => {
        if (isOwner) return 'owner';
        if (userProfile?.tier === 'vip') return 'vip';
        if (userProfile?.tier === 'premium') return 'premium';
        if (userProfile?.credits > 0) return 'standard';
        return 'free';
    }, [isOwner, userProfile?.tier, userProfile?.credits]);

    const isVIP = userTier === 'owner' || userTier === 'vip';

    const hasCredits = isOwner || (userProfile?.credits && userProfile.credits > 0);
    
    // Voice access helper
    const canUseVoice = useCallback((voiceId: string) => {
        if (isVIP) return true;
        const voiceIndex = VOICES.findIndex(v => v.id === voiceId);
        if (userTier === 'premium') return voiceIndex < 4;
        return voiceIndex < 2; // free or standard
    }, [isVIP, userTier]);

    // Dynamic Limits
    const charLimit = useMemo(() => {
        if (userTier === 'owner' || userTier === 'vip') return 10000;
        if (userTier === 'premium') return 1500;
        if (userTier === 'standard') return 500; 
        return 50; // Free limit (Reduced to save AI costs)
    }, [userTier]);

    const [isSuggesting, setIsSuggesting] = useState(false);
    const [suggestion, setSuggestion] = useState("");
    const suggestTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [vipAlert, setVipAlert] = useState<string | null>(null);

    // Memoized Footer to prevent unnecessary re-renders
    const Footer = useMemo(() => (
        <footer className="mt-20 mb-12 pb-12 px-6">
            <div className="max-w-4xl mx-auto border-t border-slate-800/40 pt-10">
                <div className="flex flex-col md:flex-row justify-between items-start gap-10">
                    {/* Left: Branding */}
                    <div className="space-y-4 max-w-[240px]">
                        <div>
                            <h3 className="text-sky-500 text-[12px] font-black uppercase tracking-[0.3em] mb-1">
                                ROM AUDIO LAB
                            </h3>
                            <p className="text-[10px] text-slate-600 uppercase tracking-widest font-black leading-tight">
                                Professional Swahili <br /> Voice AI Studio
                            </p>
                        </div>
                        <div className="pt-2">
                            <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest leading-none">
                                Powered by <span className="text-slate-400">ROM MEDIA Cloud</span>
                            </p>
                            <p className="text-[9px] text-slate-400 mt-2 font-medium">© 2026 Haki Zote Zimehifadhiwa</p>
                        </div>
                    </div>

                    {/* Center: Connect */}
                    <div className="space-y-4">
                        <h4 className="text-slate-500 text-[10px] uppercase font-black tracking-[0.2em]">Mawasiliano</h4>
                        <div className="flex flex-col gap-3">
                            <a 
                                href="mailto:info@rommedia.co.tz" 
                                className="flex items-center gap-3 text-sky-500/60 hover:text-sky-400 text-[11px] transition-all font-mono group"
                            >
                                <div className="w-7 h-7 rounded-lg bg-sky-500/5 flex items-center justify-center border border-sky-500/10 group-hover:border-sky-500/30 transition-all">
                                    <Mail className="w-3 h-3" />
                                </div>
                                info@rommedia.co.tz
                            </a>
                            <a 
                                href="https://wa.me/255784000082" 
                                target="_blank" 
                                rel="noreferrer"
                                className="flex items-center gap-3 text-emerald-500/70 hover:text-emerald-400 text-[11px] transition-all font-bold group"
                            >
                                <div className="w-7 h-7 rounded-lg bg-emerald-500/5 flex items-center justify-center border border-emerald-500/10 group-hover:border-emerald-500/30 transition-all">
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.937 3.658 1.43 5.623 1.432h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                    </svg>
                                </div>
                                WhatsApp Me
                            </a>
                        </div>
                    </div>

                    {/* Right: Resources */}
                    <div className="space-y-4 text-left md:text-right">
                        <h4 className="text-slate-500 text-[10px] uppercase font-black tracking-[0.2em]">Mengine</h4>
                        <div className="flex flex-col gap-2">
                            <a 
                                href="#" 
                                className="text-slate-600 hover:text-sky-500 text-[10px] uppercase tracking-widest font-black transition-all"
                                onClick={(e) => {
                                    e.preventDefault();
                                    alert("Privacy Policy: ROM AUDIO LAB handles your data securely via Google Gemini API. Your history is stored safely in your personal ROM AUDIO LAB account (Firebase) and is only accessible to you.");
                                }}
                            >
                                Privacy Policy
                            </a>
                            <p className="text-[11px] text-slate-500 italic max-w-[220px] md:ml-auto leading-relaxed">
                                Inatumiwa na watangazaji makini Tanzania.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    ), []);

    // AI Suggestion Logic (VIP ONLY)
    useEffect(() => {
        if (userTier === 'free' || mode !== 'tts' || text.length < 10) {
            setSuggestion("");
            return;
        }

        if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current);

        suggestTimeoutRef.current = setTimeout(async () => {
            if (text.length > 500) return; // Don't suggest for too long texts
            setIsSuggesting(true);
            try {
                const result = await getTextCompletion(text);
                // Basic cleanup of completion
                let cleanResult = result.trim();
                // Ensure it's not repeating the input
                if (cleanResult && !text.toLowerCase().includes(cleanResult.toLowerCase())) {
                    setSuggestion(cleanResult);
                }
            } catch (err) {
                console.error("Suggestion error:", err);
            } finally {
                setIsSuggesting(false);
            }
        }, 2000); // 2s debounce to be conservative with costs

        return () => {
            if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current);
        };
    }, [text, mode, userTier]);

    const checkTestUsage = () => {
        if (user || isOwner) return true;
        
        const usageCount = parseInt(localStorage.getItem('sauti_test_usage') || '0');
        if (usageCount >= 3) {
            setError("Muda wako wa majaribio umeisha (Test time is over). Tafadhali ingia na akaunti yako au nunua kifurushi ili kuendelea kufurahia huduma zetu.");
            setShowTopUp(true);
            return false;
        }
        localStorage.setItem('sauti_test_usage', (usageCount + 1).toString());
        return true;
    };

    const handleGenerateSpeech = async () => {
        if (!text.trim()) {
            setError('Please enter some text.');
            return;
        }

        if (text.length > charLimit) {
            setError(`Maandishi ni marefu sana kwa akaunti yako. Kifurushi chako kinaruhusu herufi ${charLimit}. Pata kifurushi kikubwa zaidi kufurahia herufi nyingi zaidi.`);
            setShowTopUp(true);
            return;
        }

        if (!checkTestUsage()) return;

        // Check voice access
        if (!canUseVoice(selectedVoice)) {
            setVipAlert(`Sauti hii ni kwa watumiaji wa ${selectedVoice === 'Fenrir' || selectedVoice === 'Child' ? 'VIP' : 'Premium'} tu. Panda kifurushi sasa ili kuifungua!`);
            return;
        }

        // Logic: 1 credit per 500 characters
        const requiredCredits = isOwner ? 0 : Math.ceil(text.length / 500);

        if (!isOwner && (!userProfile?.credits || userProfile.credits < requiredCredits)) {
            setError(`Huna credits za kutosha. Unahitaji credits ${requiredCredits} kwa maneno haya (${text.length} characters). Salio lako ni ${userProfile?.credits || 0}.`);
            setShowTopUp(true);
            return;
        }

        setIsLoading(true);
        setError(null);
        setAudioData(null);
        setIsPlaying(false);
        setIsSpeechSaved(false);

        // Fix for iOS: Initialize / Resume AudioContext immediately during user gesture
        try {
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
            }
            if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
            }
        } catch (e) {
            console.warn("Could not init AudioContext early:", e);
        }

        if (audioSourceRef.current) {
            audioSourceRef.current.stop();
        }

        try {
            let processedText = text;
            if (userTier === 'free') {
                // Watermark for AI pronunciation optimization
                processedText = `rom midia oudio lab. . . ${text}`;
            }

            const base64Audio = await generateSpeech(processedText, selectedVoice, selectedTone, useManualSeed ? voiceSeed : undefined);
            setAudioData(base64Audio);
            const audioBytes = decodeBase64(base64Audio);
            
            // Re-ensure context exists after await for safety
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
            }
            
            audioBufferRef.current = await decodePcmToAudioBuffer(audioBytes, audioContextRef.current);
            
            // Auto-save to history
            if (user) {
                try {
                    // Firestore has a 1MB limit per document. 
                    // Base64 encoding adds ~33% overhead. 
                    // If audioData is too large, we store history WITHOUT the audio data to prevent crash.
                    const MAX_FIRESTORE_SIZE = 950000; // ~950KB safety margin
                    const shouldIncludeAudioData = base64Audio.length < MAX_FIRESTORE_SIZE;

                    await saveAudioHistory(user.uid, {
                        text,
                        voiceId: selectedVoice,
                        tone: selectedTone,
                        seed: useManualSeed ? voiceSeed : undefined,
                        audioData: shouldIncludeAudioData ? base64Audio : null,
                        isAudioTooLarge: !shouldIncludeAudioData
                    });
                    setIsSpeechSaved(true);
                } catch (historyErr) {
                    console.warn("Auto-save to history failed, but audio remains available:", historyErr);
                    // Silently fail history save so user isn't interrupted by error box
                }
            }

            // Deduct credits based on text length
            if (!isOwner && user) {
                await deductCredit(user.uid, requiredCredits);
            }
        } catch (err) {
            console.error(err);
            let userMsg = err instanceof Error ? err.message : 'An unknown error occurred.';
            
            // Clean up JSON strings if they leak from the backend
            // EXTREMELY AGGRESSIVE FILTERING:
            const isJson = (str: string) => {
                try {
                    const o = JSON.parse(str);
                    return (o && typeof o === "object");
                } catch (e) { return false; }
            };

            if (isJson(userMsg) || userMsg.trim().startsWith('{') || userMsg.includes('"error"') || userMsg.includes('"message"')) {
                // If the JSON actually contains our Swahili error, use it!
                const lower = userMsg.toLowerCase();
                if (lower.includes("kosa") || lower.includes("samahani") || lower.includes("umekwama") || lower.includes("mfumo")) {
                    // It's a specific message, allow it to continue to specific handlers or display
                } else {
                    userMsg = "SAMAHANI: Mfumo wetu wa AI umepata tatizo la muda kidogo. Jaribu tena sasa au wasiliana na ROM Media kwa msaada.";
                }
            }

            // Custom Keyword Protection (Always show these)
            const uMsg = userMsg.toUpperCase();
            if (uMsg.includes("KOSA") || uMsg.includes("SAMAHANI") || uMsg.includes("UMEKWAMA")) {
                setError(userMsg);
                return;
            }

            // Handle 503 Service Unavailable (High Demand)
            if (userMsg.toLowerCase().includes('503') || userMsg.toLowerCase().includes('high demand') || userMsg.toLowerCase().includes('overloaded')) {
                userMsg = "Google seva ziko busy sana (High Demand). Tafadhali subiri sekunde 5 kisha bonyeza kitufe cha 'Generate' tena.";
            }

            setError(userMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTranslate = async (lang: 'Swahili' | 'English') => {
        if (!text.trim() || isLoading) return;
        
        // Tier check for Translation
        if (userTier === 'free' || userTier === 'standard') {
            setVipAlert("Huduma ya Tafsiri (Translation) inapatikana kwa watumiaji wa Premium na VIP tu. Panda kifurushi sasa ili kurahisisha kazi zako!");
            return;
        }

        const translationLimit = isOwner ? 10000 : 2000;
        if (text.length > translationLimit) {
            setError(`Maandishi ni marefu sana kwa tafsiri. Tafadhali punguza yawe chini ya herufi ${translationLimit}.`);
            return;
        }

        const requiredCredits = isOwner ? 0 : 1; 

        if (!isOwner && (!userProfile?.credits || userProfile.credits < requiredCredits)) {
            setError('Huna credits za kutosha kutumia huduma ya tafsiri.');
            setShowTopUp(true);
            return;
        }

        if (!checkTestUsage()) return;

        setIsLoading(true);
        setError(null);
        try {
            const translated = await translateText(text, lang);
            setText(translated);

            // Deduct credits for translation
            if (!isOwner && user) {
                await deductCredit(user.uid, requiredCredits);
            }
        } catch (err) {
            console.error(err);
            let userMsg = err instanceof Error ? err.message : 'Translation failed';
            
            // Check for Swahili keywords before generic masking
            const uMsg = userMsg.toUpperCase();
            if (uMsg.includes("KOSA") || uMsg.includes("SAMAHANI") || uMsg.includes("UMEKWAMA")) {
                setError(userMsg);
            } else if (userMsg.trim().startsWith('{') || userMsg.includes('"error"') || userMsg.includes('"message"')) {
                userMsg = "SAMAHANI: Huduma ya kutafsiri imepata hitilafu kidogo mara hii. Tafadhali jaribu tena.";
                setError(userMsg);
            } else {
                setError(userMsg);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePlayPause = useCallback(async () => {
        if (!audioContextRef.current || !audioBufferRef.current) return;
        
        // iOS Fix: Must resume on every play attempt
        if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
        }
        
        if (isPlaying) {
            audioSourceRef.current?.stop();
            setIsPlaying(false);
        } else {
            const source = audioContextRef.current.createBufferSource();
            source.buffer = audioBufferRef.current;
            source.playbackRate.value = playbackRate;
            source.connect(audioContextRef.current.destination);
            source.start();
            source.onended = () => {
                setIsPlaying(false);
                audioSourceRef.current = null;
            };
            audioSourceRef.current = source;
            setIsPlaying(true);
        }
    }, [isPlaying, playbackRate]);

    const handleDownloadWav = () => {
        if (!audioBufferRef.current) return;
        try {
            const wavBlob = audioBufferToWavBlob(audioBufferRef.current);
            const url = URL.createObjectURL(wavBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'swahili_speech.wav';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            setError('Failed to create download file.');
        }
    };

    const handleDownloadMp3 = async () => {
        if (!audioBufferRef.current || isEncodingMp3) return;

        setIsEncodingMp3(true);
        setError(null);
        try {
            await new Promise(resolve => setTimeout(resolve, 50));
            const mp3Blob = audioBufferToMp3Blob(audioBufferRef.current);
            const url = URL.createObjectURL(mp3Blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'swahili_speech.mp3';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            setError('Failed to create MP3 download file.');
        } finally {
            setIsEncodingMp3(false);
        }
    };

    const handleSaveSpeech = async () => {
        if (!text.trim() || !user || !audioData) return;
        try {
            await saveAudioHistory(user.uid, {
                text,
                voiceId: selectedVoice,
                tone: selectedTone,
                seed: useManualSeed ? voiceSeed : undefined,
                audioData: audioData
            });
            setIsSpeechSaved(true);
        } catch (err) {
            setError('Failed to save to cloud.');
        }
    };

    const handleDeleteSpeech = async (id: string) => {
        if (!user) return;
        try {
            await deleteHistoryItem(user.uid, id, 'history');
        } catch (err) {
            console.error(err);
        }
    };

    const handleRestoreSpeech = (item: SavedSpeech) => {
        setText(item.text);
        setSelectedVoice(item.voice);
        setSelectedTone(item.tone || 'standard');
        if (item.seed !== undefined) {
            setVoiceSeed(item.seed);
            setUseManualSeed(true);
        } else {
            setUseManualSeed(false);
        }
        setMode('tts');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setTranscriptionFile(e.target.files[0]);
            setTranscriptionResult('');
            setTranscriptionError(null);
            setIsSaved(false);
        }
    };

    const handleTranscribe = async () => {
        if (!transcriptionFile) return;

        // Tier check for Transcription
        if (userTier === 'free' || userTier === 'standard' || userTier === 'premium') {
            setVipAlert("Huduma ya Transcribe (Voice to Text) inapatikana kwa watumiaji wa VIP pekee. Panda kifurushi sasa ili upate huduma hii ya kipekee!");
            return;
        }

        const requiredCredits = isOwner ? 0 : 2; 

        if (!isOwner && (!userProfile?.credits || userProfile.credits < requiredCredits)) {
            setTranscriptionError(`Huna credits za kutosha kufanya Transcription. Unahitaji credits ${requiredCredits}.`);
            setShowTopUp(true);
            return;
        }

        setIsTranscribing(true);
        setTranscriptionError(null);
        setIsSaved(false);

        try {
            const reader = new FileReader();
            reader.readAsDataURL(transcriptionFile);
            
            reader.onload = async () => {
                try {
                    const base64String = (reader.result as string).split(',')[1];
                    const mimeType = transcriptionFile.type;
                    const result = await transcribeAudio(base64String, mimeType);
                    setTranscriptionResult(result);

                    // Auto-save to history
                    if (user) {
                        await saveTranscriptionHistory(user.uid, result);
                        setIsSaved(true);
                    }

                    // Deduct credits for transcription
                    if (!isOwner && user) {
                        await deductCredit(user.uid, requiredCredits); // Transcription is heavier
                    }
                } catch (err: any) {
                    let msg = err.message || 'Transcription failed';
                    
                    // Check for Swahili keywords before generic masking
                    const uMsg = msg.toUpperCase();
                    if (uMsg.includes("KOSA") || uMsg.includes("SAMAHANI") || uMsg.includes("UMEKWAMA")) {
                        setTranscriptionError(msg);
                    } else if (msg.trim().startsWith('{') || msg.includes('"error"') || msg.includes('"message"')) {
                        msg = "SAMAHANI: Mfumo umeshindwa kusoma sauti hii kwa sasa. Jaribu tena au wasiliana nasi.";
                        setTranscriptionError(msg);
                    } else {
                        setTranscriptionError(msg);
                    }
                } finally {
                    setIsTranscribing(false);
                }
            };

            reader.onerror = () => {
                setTranscriptionError("Failed to read file.");
                setIsTranscribing(false);
            };

        } catch (err) {
            setTranscriptionError('An unexpected error occurred.');
            setIsTranscribing(false);
        }
    };

    const handleSaveTranscription = async () => {
        if (!transcriptionResult || !user) return;
        try {
            await saveTranscriptionHistory(user.uid, transcriptionResult);
            setIsSaved(true);
        } catch (err) {
            setTranscriptionError('Failed to save to cloud.');
        }
    };

    const handleDeleteTranscription = async (id: string) => {
        if (!user) return;
        try {
            await deleteHistoryItem(user.uid, id, 'transcriptions');
        } catch (err) {
            console.error(err);
        }
    };

    const handleRestoreTranscription = (item: SavedTranscription) => {
        setTranscriptionResult(item.text);
        setMode('transcribe');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const AdminPortal: React.FC = () => {
        const [searchEmail, setSearchEmail] = useState('');
        const [foundUser, setFoundUser] = useState<any>(null);
        const [searchLoading, setSearchLoading] = useState(false);
        const [addAmount, setAddAmount] = useState(10);
        const [adminError, setAdminError] = useState<string | null>(null);
        const [adminSuccess, setAdminSuccess] = useState<string | null>(null);

        const handleSearch = async () => {
            if (!searchEmail.trim()) return;
            setSearchLoading(true);
            setAdminError(null);
            setAdminSuccess(null);
            try {
                const result = await searchUserByEmail(searchEmail);
                if (result) {
                    setFoundUser(result);
                } else {
                    setAdminError('Mtumiaji hajapatikana (User not found). Hakikisha email ni sahihi.');
                    setFoundUser(null);
                }
            } catch (err: any) {
                console.error("Admin search error:", err);
                try {
                    const firestoreErr = JSON.parse(err.message);
                    setAdminError(`Hitilafu ya ruhusa: ${firestoreErr.error}. Hakikisha wewe ni admin.`);
                } catch {
                    // If it's not a JSON error from handleFirestoreError, show the string representation
                    const msg = err.message || String(err);
                    setAdminError(`Kuna hitilafu: ${msg}. Tafadhali jaribu tena.`);
                }
            } finally {
                setSearchLoading(false);
            }
        };

        const handleAddCredits = async () => {
            if (!foundUser || addAmount <= 0) return;
            setSearchLoading(true);
            try {
                await addCredits(foundUser.id, addAmount);
                setAdminSuccess(`Credits ${addAmount} zimeongezwa kwa ${foundUser.email} kwa mafanikio!`);
                // Refresh local found user view
                setFoundUser({ ...foundUser, credits: (foundUser.credits || 0) + addAmount });
            } catch (err) {
                console.error("Add credits error:", err);
                setAdminError(`Hukuweza kuongeza credits. Angalia ruhusa zako. (${err instanceof Error ? err.message : String(err)})`);
            } finally {
                setSearchLoading(false);
            }
        };

        const handleUpdateTier = async (newTier: 'free' | 'standard' | 'premium' | 'vip') => {
            if (!foundUser) return;
            setSearchLoading(true);
            setAdminError(null);
            setAdminSuccess(null);
            try {
                await updateUserTier(foundUser.id, newTier);
                setAdminSuccess(`Kifurushi (Tier) cha ${foundUser.email} kimebadilishwa kuwa ${newTier.toUpperCase()} kwa mafanikio!`);
                // Refresh local found user view
                setFoundUser({ ...foundUser, tier: newTier });
            } catch (err) {
                console.error("Update tier error:", err);
                setAdminError(`Hukuweza kubadilisha kifurushi. Angalia ruhusa zako. (${err instanceof Error ? err.message : String(err)})`);
            } finally {
                setSearchLoading(false);
            }
        };

        return (
            <div className="space-y-8 animate-fade-in">
                <div className="bg-slate-900/60 p-6 rounded-2xl border border-sky-500/20 shadow-xl">
                    <div className="flex items-center gap-3 mb-6">
                        <ShieldCheck className="w-8 h-8 text-sky-400" />
                        <div>
                            <h2 className="text-xl font-black text-slate-100 uppercase tracking-tight">Admin Control Panel</h2>
                            <p className="text-xs text-slate-400">Manage user credits and access.</p>
                        </div>
                    </div>

                    <div className="mb-6 p-4 bg-sky-500/5 rounded-xl border border-sky-500/10">
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Signed in as Admin:</p>
                        <p className="text-sm text-sky-400 font-mono">{user?.email || 'Unknown'}</p>
                    </div>

                    <div className="space-y-4">
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Search User by Email</label>
                        <div className="flex gap-2">
                            <input 
                                type="email"
                                value={searchEmail}
                                onChange={(e) => setSearchEmail(e.target.value)}
                                placeholder="customer@email.com"
                                className="flex-1 bg-slate-950/50 border border-slate-700 p-4 rounded-xl text-slate-200 outline-none focus:ring-2 focus:ring-sky-500"
                            />
                            <button 
                                onClick={handleSearch}
                                disabled={searchLoading}
                                className="bg-sky-600 hover:bg-sky-500 text-white p-4 rounded-xl transition-all disabled:opacity-50"
                            >
                                {searchLoading ? <Spinner /> : <Search className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>

                    {adminError && <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-xs text-center">{adminError}</div>}
                    {adminSuccess && <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 text-xs text-center font-bold">{adminSuccess}</div>}

                    {foundUser && (
                        <div className="mt-8 p-6 bg-slate-950/40 rounded-2xl border border-slate-700 animate-in zoom-in-95">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-sky-500/10 rounded-full flex items-center justify-center text-sky-400 text-xl font-black">
                                        {foundUser.email[0].toUpperCase()}
                                    </div>
                                    <div>
                                        <p className="text-slate-100 font-bold">{foundUser.displayName || 'Unnamed User'}</p>
                                        <p className="text-xs text-slate-500">{foundUser.email}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="text-center">
                                        <p className="text-[10px] text-slate-500 uppercase font-black">Current Credits</p>
                                        <p className="text-2xl font-black text-sky-400 font-mono leading-none">{foundUser.credits || 0}</p>
                                    </div>
                                    <div className="text-center border-l border-slate-800 pl-4 animate-fade-in">
                                        <p className="text-[10px] text-slate-500 uppercase font-black">Current Tier</p>
                                        <p className="text-base font-black text-amber-500 uppercase leading-none mt-1 font-mono">{foundUser.tier || 'free'}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-slate-800 space-y-4">
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Amount to Add</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {[10, 50, 100, 500].map(amt => (
                                        <button 
                                            key={amt}
                                            onClick={() => setAddAmount(amt)}
                                            className={`py-2 rounded-lg text-xs font-bold transition-all ${addAmount === amt ? 'bg-sky-500 text-white shadow-lg shadow-sky-500/20' : 'bg-slate-900 text-slate-400'}`}
                                        >
                                            +{amt}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <input 
                                        type="number"
                                        value={addAmount}
                                        onChange={(e) => setAddAmount(parseInt(e.target.value) || 0)}
                                        className="flex-1 bg-slate-950/50 border border-slate-700 p-4 rounded-xl text-slate-200 outline-none focus:ring-1 focus:ring-emerald-500"
                                    />
                                    <button 
                                        onClick={handleAddCredits}
                                        disabled={searchLoading || addAmount <= 0}
                                        className="bg-emerald-600 hover:bg-emerald-500 text-white font-black px-6 py-4 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 group shadow-lg shadow-emerald-600/20"
                                    >
                                        <PlusCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
                                        <div className="flex flex-col items-start leading-none">
                                            <span className="text-xs opacity-70 font-bold">ADD CREDITS</span>
                                            <span className="text-lg tracking-tighter">ONGEZA SALIO</span>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-slate-800 space-y-4">
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Change Account Tier / Badilisha Kifurushi</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {(['free', 'standard', 'premium', 'vip'] as const).map(t => (
                                        <button 
                                            key={t}
                                            onClick={() => handleUpdateTier(t)}
                                            disabled={searchLoading}
                                            className={`py-3 rounded-xl text-xs font-black transition-all uppercase tracking-tight border ${foundUser.tier === t || (!foundUser.tier && t === 'free') ? 'bg-amber-500 text-slate-950 border-amber-500 shadow-lg shadow-amber-500/10 font-bold' : 'bg-slate-950/50 text-slate-400 border-slate-800 hover:border-slate-700'}`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[10px] text-slate-500 leading-normal">
                                    * <strong>FREE:</strong> Kikomo cha 50 characters.<br />
                                    * <strong>STANDARD:</strong> Kikomo cha 500 characters.<br />
                                    * <strong>PREMIUM:</strong> Kikomo cha 1,500 characters.<br />
                                    * <strong>VIP:</strong> Kikomo cha 10,000 characters.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleCopyText = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const handleClearAllHistory = () => {
        if (window.confirm('Wipe ALL cloud history? This is irreversible.')) {
            // In a real app we'd batch delete, for now we let users delete one by one or clear their local cache if needed
            alert("For security, batch deletion is disabled. Please delete items individually.");
        }
    };

    if (isAuthLoading) {
        return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Spinner /></div>;
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
                <div className="w-full max-w-md bg-slate-800/50 backdrop-blur-md rounded-3xl p-10 border border-slate-700 shadow-2xl text-center space-y-8">
                    <div className="flex flex-col items-center justify-center">
                        <div className="w-56 h-56 flex items-center justify-center -mt-10 -mb-8">
                            <img 
                                src="/logo.png" 
                                alt="Logo" 
                                className="w-full h-full object-contain"
                                referrerPolicy="no-referrer"
                            />
                        </div>
                        <div className="text-[11px] text-sky-500 font-black uppercase tracking-[0.3em] mb-1">TAMKA TUSIKIE</div>
                        <h1 className="text-3xl font-black text-sky-400">ROM AUDIO LAB</h1>
                        <p className="text-slate-400 mt-2">Ingia kuanza kutumia Studio yetu.</p>
                    </div>
                    <button 
                        onClick={signInWithGoogle}
                        className="w-full flex items-center justify-center gap-4 bg-white text-slate-900 font-bold py-4 px-6 rounded-2xl hover:bg-slate-200 transition-all active:scale-95"
                    >
                        <svg className="w-6 h-6" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/><path fill="#1976D2" d="M43.611,20.083L43.611,20.083L42,20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/></svg>
                        Log in with Google
                    </button>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black">
                        Powered by ROM MEDIA Cloud
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 font-sans selection:bg-sky-500/30">
            {/* VIP Alert Popup */}
            {vipAlert && (
                <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="bg-amber-500 text-black font-black px-6 py-4 rounded-2xl shadow-[0_0_50px_rgba(245,158,11,0.3)] border-2 border-amber-400 flex items-center gap-4 max-w-md mx-4">
                        <div className="w-10 h-10 bg-black/10 rounded-full flex items-center justify-center shrink-0">
                            <ShieldCheck className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs uppercase tracking-tighter leading-none mb-1 opacity-70">Ujumbe wa Mfumo</p>
                            <p className="text-sm leading-tight">{vipAlert}</p>
                        </div>
                        <button 
                            onClick={() => {
                                setVipAlert(null);
                                setShowTopUp(true);
                            }}
                            className="bg-black text-white text-[10px] px-3 py-2 rounded-xl hover:bg-slate-800 transition-colors uppercase font-bold"
                        >
                            Upgrade
                        </button>
                    </div>
                </div>
            )}
            {/* Top Bar for User */}
            <div className="w-full max-w-3xl flex justify-between items-center mb-4 px-2">
                <div className="flex items-center gap-3">
                    <img src={user.photoURL || ''} className="w-8 h-8 rounded-full border border-sky-500/30" alt="User" />
                    <div>
                        <p className="text-xs font-bold text-slate-200">{user.displayName}</p>
                        <div className="flex items-center gap-2">
                                <div className="flex flex-col">
                                    <div className="flex items-center gap-1 bg-slate-950/50 px-2 py-0.5 rounded-full border border-slate-700/50">
                                        <Wallet className="w-2.5 h-2.5 text-sky-400" />
                                        <p className="text-[10px] text-slate-300 font-mono">
                                            {isOwner ? 'UNLIMITED 👑' : `${userProfile?.credits || 0} Credits`}
                                        </p>
                                    </div>
                                    <span className={cn(
                                        "text-[8px] font-black uppercase tracking-widest mt-0.5 ml-1",
                                        userTier === 'premium' ? "text-amber-400" : 
                                        userTier === 'standard' ? "text-sky-400" : "text-slate-500"
                                    )}>
                                        {userTier} Account
                                    </span>
                                </div>
                            {!isOwner && (
                                <button 
                                    onClick={() => {
                                        setShowTopUp(true);
                                        if (user) logPaymentInterest(user);
                                    }}
                                    className="text-[12px] bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2 rounded-xl transition-all font-black uppercase shadow-xl shadow-emerald-500/30 active:scale-95 flex items-center gap-3 border border-emerald-400/30"
                                >
                                    <span className="tracking-widest">Refill</span>
                                    <span className="border-l-2 border-white pl-3 py-0.5 text-[11px] font-black">Ongeza Salio</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <button 
                    onClick={logout}
                    className="text-[10px] font-black text-slate-500 hover:text-red-400 uppercase tracking-widest transition-colors"
                >
                    Sign Out
                </button>
            </div>

            {/* API Key Warning */}
            {apiKeyError && (
                <div className="w-full max-w-3xl mb-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-center gap-4 text-amber-200">
                    <AlertCircle className="w-6 h-6 shrink-0" />
                    <div className="flex-1">
                        <p className="text-sm font-bold">API Key Missing / Haijawekwa Sahihi</p>
                        <p className="text-xs opacity-80">
                            Nenda kwenye <b>Settings</b> -&gt; <b>Secrets</b>. 
                            Bofya <b>Add Secret</b> kama haipo, weka <b>GEMINI_API_KEY</b> kwenye 'Name' na ile kodi yako ya AI Studio (AIza...) kwenye 'Value'.
                        </p>
                    </div>
                </div>
            )}

            <div className="w-full max-w-3xl bg-slate-800/50 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden border border-slate-700">
                {/* Header */}
                <div className="p-8 pb-4 text-center flex flex-col items-center">
                    <div className="w-52 h-52 -mt-10 -mb-8 flex items-center justify-center">
                        <img 
                            src="/logo.png" 
                            alt="ROM AUDIO LAB Logo" 
                            className="w-full h-full object-contain"
                            referrerPolicy="no-referrer"
                        />
                    </div>
                    <div className="mb-2">
                        <span className="bg-sky-500/10 text-sky-400 text-[10px] px-3 py-1 rounded-full font-black tracking-widest uppercase border border-sky-500/20">
                            TAMKA TUSIKIE
                        </span>
                    </div>
                    <h1 className="text-4xl font-black text-sky-400 tracking-tighter">ROM AUDIO LAB</h1>
                    <p className="text-slate-400 mt-2 font-bold uppercase tracking-[0.2em] text-[10px]">Professional Swahili Voice Studio</p>
                </div>

                {/* Main Navigation Tabs */}
                <div className="flex border-b border-slate-700 bg-slate-900/20">
                    <button
                        onClick={() => setMode('tts')}
                        className={`flex-1 py-4 flex items-center justify-center gap-2 text-sm font-bold transition-all duration-300 ${
                            mode === 'tts'
                                ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400'
                                : 'text-slate-500 hover:text-slate-300'
                        }`}
                    >
                        <FileText className="w-5 h-5" />
                        <span className="hidden sm:inline">Text to Speech</span>
                        <span className="sm:hidden">TTS</span>
                    </button>
                    <button
                        onClick={() => setMode('transcribe')}
                        className={`flex-1 py-4 flex items-center justify-center gap-2 text-sm font-bold transition-all duration-300 ${
                            mode === 'transcribe'
                                ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400'
                                : 'text-slate-500 hover:text-slate-300'
                        }`}
                    >
                        <Mic className="w-5 h-5" />
                        <span className="hidden sm:inline">Transcribe</span>
                        <span className="sm:hidden">Voice</span>
                    </button>
                    <button
                        onClick={() => setMode('history')}
                        className={`flex-1 py-4 flex items-center justify-center gap-2 text-sm font-bold transition-all duration-300 ${
                            mode === 'history'
                                ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400'
                                : 'text-slate-500 hover:text-slate-300'
                        }`}
                    >
                        <Clock className="w-5 h-5" />
                        <span className="hidden sm:inline">History Library</span>
                        <span className="sm:hidden">History</span>
                    </button>
                    {isOwner && (
                        <button
                            onClick={() => setMode('admin')}
                            className={`flex-1 py-4 flex items-center justify-center gap-2 text-sm font-bold transition-all duration-300 ${
                                mode === 'admin'
                                    ? 'bg-emerald-900/20 text-emerald-400 border-b-2 border-emerald-400'
                                    : 'text-slate-500 hover:text-emerald-300'
                            }`}
                        >
                            <ShieldCheck className="w-5 h-5" />
                            <span>Admin</span>
                        </button>
                    )}
                </div>

                <div className="p-6 md:p-10 space-y-8 min-h-[500px]">
                    {/* TTS Mode */}
                    {mode === 'tts' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block mb-2 text-xs font-bold text-slate-500 uppercase tracking-widest">Voice & Tone</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <select
                                            value={selectedVoice}
                                            onChange={(e) => setSelectedVoice(e.target.value)}
                                            disabled={isLoading}
                                            className="w-full p-3 bg-slate-900/70 border border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all duration-300 text-slate-200 text-sm"
                                        >
                                            {VOICES.map((v) => (
                                                <option key={v.id} value={v.id}>
                                                    {v.name} {!canUseVoice(v.id) ? '🔒' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <select
                                            value={selectedTone}
                                            onChange={(e) => setSelectedTone(e.target.value)}
                                            disabled={isLoading}
                                            className="w-full p-3 bg-slate-900/70 border border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all duration-300 text-slate-200 text-sm"
                                        >
                                            {TONES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="mt-3 p-2.5 bg-sky-500/5 rounded-lg border border-sky-500/10">
                                        <p className="text-[11px] text-sky-400/80 leading-relaxed italic">
                                            {VOICES.find(v => v.id === selectedVoice)?.desc}
                                        </p>
                                    </div>
                                    {!canUseVoice(selectedVoice) && (
                                        <p className="text-[10px] text-amber-500 font-bold mt-2 px-1 flex items-center gap-1.5 animate-pulse">
                                            <ShieldCheck className="w-3.5 h-3.5" />
                                            Sauti hii inahitaji kifurushi kikubwa zaidi!
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block mb-2 text-xs font-bold text-slate-500 uppercase tracking-widest">Playback Speed ({playbackRate.toFixed(1)}x)</label>
                                    <input
                                        type="range" min="0.5" max="2" step="0.1" value={playbackRate}
                                        onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                                        className="w-full h-2 mt-4 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-sky-500"
                                    />
                                </div>
                            </div>

                            <div 
                                onClick={() => {
                                    if (!isVIP) {
                                        setVipAlert("Huduma ya 'Voice Seed' ni kwa wateja wa VIP tu ili kuleta utulivu wa sauti (Consistency). Panda kifurushi!");
                                        setTimeout(() => setVipAlert(null), 5000);
                                    }
                                }}
                                className={`bg-slate-900/40 p-4 rounded-xl border border-slate-700/50 flex flex-col sm:flex-row items-center justify-between gap-4 relative transition-all ${!isVIP ? 'opacity-40 cursor-pointer hover:bg-slate-800/60' : ''}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div 
                                        className="relative flex items-center gap-3 cursor-pointer"
                                        onClick={() => {
                                            if (!isVIP) {
                                                setVipAlert("Manual Voice Seed ni feature ya VIP tu. Inakupa uwezo wa kudhibiti kiini cha sauti kwa usahihi wa hali ya juu!");
                                            } else {
                                                setUseManualSeed(!useManualSeed);
                                            }
                                        }}
                                    >
                                        <div className={`w-8 h-4 rounded-full transition-colors relative ${useManualSeed && isVIP ? 'bg-sky-500' : 'bg-slate-700'}`}>
                                            <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${useManualSeed && isVIP ? 'left-4.5' : 'left-0.5'}`} />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <label className={`text-sm font-bold text-slate-300 cursor-pointer`}>Use Manual Voice Seed</label>
                                            {!isVIP && <ShieldCheck className="w-4 h-4 text-amber-500" />}
                                        </div>
                                    </div>
                                </div>
                                
                                {useManualSeed && (
                                    <div className={`flex items-center gap-2 w-full sm:w-auto ${!isVIP ? 'pointer-events-none' : ''}`}>
                                        <input 
                                            type="number"
                                            value={voiceSeed}
                                            onChange={(e) => setVoiceSeed(parseInt(e.target.value) || 0)}
                                            className="w-full sm:w-32 p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 text-sm focus:ring-1 focus:ring-sky-500 outline-none"
                                            placeholder="Seed value"
                                            disabled={!useManualSeed || !isVIP}
                                        />
                                        <button 
                                            onClick={() => setVoiceSeed(Math.floor(Math.random() * 1000000))}
                                            className={`p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors ${!isVIP ? 'opacity-50' : ''}`}
                                            title="Randomize Seed"
                                            disabled={!useManualSeed || !isVIP}
                                        >
                                            <RotateCcw className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                                {!useManualSeed && (
                                    <p className="text-xs text-slate-500 italic">Deterministic seed will be used for consistency.</p>
                                )}
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between items-end px-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest">Text to Speak</label>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => handleTranslate('Swahili')}
                                            disabled={isLoading || !text.trim()}
                                            className="text-[10px] bg-slate-800/80 hover:bg-sky-500/20 text-sky-400 py-1.5 px-3 rounded-lg border border-sky-400/20 transition-all disabled:opacity-50 flex items-center gap-1.5 font-bold relative group"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 11.37 9.188 15.287 5.706 17.652M4.921 11.02a11.45 11.45 0 001.3 4.298M16.102 4.499c1.168.195 2.327.439 3.468.731" /></svg>
                                            Transl. Swahili
                                            {(userTier === 'free' || userTier === 'standard') && <ShieldCheck className="w-2.5 h-2.5 text-amber-500 ml-0.5" />}
                                        </button>
                                        <button 
                                            onClick={() => handleTranslate('English')}
                                            disabled={isLoading || !text.trim()}
                                            className="text-[10px] bg-slate-800/80 hover:bg-sky-500/20 text-sky-400 py-1.5 px-3 rounded-lg border border-sky-400/20 transition-all disabled:opacity-50 flex items-center gap-1.5 font-bold relative group"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 11.37 9.188 15.287 5.706 17.652M4.921 11.02a11.45 11.45 0 001.3 4.298M16.102 4.499c1.168.195 2.327.439 3.468.731" /></svg>
                                            Transl. English
                                            {(userTier === 'free' || userTier === 'standard') && <ShieldCheck className="w-2.5 h-2.5 text-amber-500 ml-0.5" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="relative">
                                    {/* AI Suggestion UI (VIP) */}
                                    {(userTier === 'premium' || userTier === 'owner' || userTier === 'standard') && (
                                        <div className="flex justify-between items-center mb-1.5 h-6">
                                            {isSuggesting && (
                                                <div className="flex items-center gap-1.5 text-[9px] text-sky-400 font-bold uppercase animate-pulse">
                                                    <div className="w-2 h-2 rounded-full bg-sky-400 animate-bounce" />
                                                    AI inatabiri...
                                                </div>
                                            )}
                                            {!isSuggesting && suggestion && (
                                                <button 
                                                    onClick={() => {
                                                        const space = text.endsWith(' ') ? '' : ' ';
                                                        setText(prev => prev + space + suggestion);
                                                        setSuggestion("");
                                                    }}
                                                    className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 text-[10px] px-2 py-0.5 rounded border border-sky-500/30 flex items-center gap-2 transition-all group"
                                                >
                                                    <span className="font-bold">KAMILISHA:</span>
                                                    <span className="italic opacity-80 line-clamp-1 truncate max-w-[150px]">...{suggestion}</span>
                                                    <Check className="w-2.5 h-2.5" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <textarea
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder="Andika maandishi hapa..."
                                        className="w-full h-48 p-5 bg-slate-900/70 border border-slate-700 rounded-2xl focus:ring-2 focus:ring-sky-500 outline-none transition-all duration-300 resize-none text-xl placeholder-slate-600 shadow-inner"
                                        disabled={isLoading}
                                    />
                                    <div className={`absolute bottom-4 right-4 text-[10px] font-black tracking-widest px-2 py-1 rounded-md border ${
                                        text.length > charLimit 
                                        ? 'bg-red-500/10 text-red-500 border-red-500/20' 
                                        : 'bg-slate-950/50 text-slate-500 border-slate-700/50'
                                    }`}>
                                        {text.length} / {charLimit}
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={handleGenerateSpeech}
                                    disabled={isLoading || !text.trim()}
                                    className="flex-1 flex items-center justify-center gap-3 bg-sky-600 text-white font-black py-4 px-6 rounded-2xl hover:bg-sky-500 disabled:bg-slate-700 disabled:text-slate-500 transition-all duration-300 shadow-lg shadow-sky-900/20 active:scale-[0.98]"
                                >
                                    {isLoading ? <><Spinner /> PROCESSING</> : "GENERATE AUDIO"}
                                </button>

                                {audioData && (
                                    <button
                                        onClick={() => {
                                            setText('');
                                            setAudioData(null);
                                            setError(null);
                                        }}
                                        className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-6 rounded-2xl border border-slate-700 transition-all active:scale-95 flex flex-col items-center justify-center leading-none"
                                        title="Anza Upya"
                                    >
                                        <RotateCcw className="w-5 h-5 mb-1 opacity-50" />
                                        <span className="text-[10px] uppercase font-black">Anza Upya</span>
                                    </button>
                                )}
                            </div>

                            {error && (
                                <div className="p-5 bg-rose-500/10 border border-rose-500/30 rounded-2xl flex flex-col gap-3 animate-in fade-in slide-in-from-top-4 duration-300 shadow-[0_0_30px_rgba(244,63,94,0.1)]">
                                    <div className="flex items-start gap-4">
                                        <AlertCircle className="w-6 h-6 text-rose-500 shrink-0 mt-0.5" />
                                        <p className="text-rose-200 text-sm font-bold leading-relaxed">{error}</p>
                                    </div>
                                    {error.includes("KOSA") && (
                                        <div className="mt-1 pt-3 border-t border-rose-500/20 text-[11px] text-rose-300 font-medium">
                                            <p className="uppercase tracking-[0.2em] mb-2 opacity-70 font-black flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                                                Jinsi ya kurekebisha:
                                            </p>
                                            <ol className="list-decimal ml-4 space-y-2 leading-relaxed">
                                                <li>Nenda kwenye <b>Settings</b> (ki-icon cha gia pembeni au chini ya screen).</li>
                                                <li>Kwenye upande wa <b>Secrets</b>, tafuta mstari wa <b>GEMINI_API_KEY</b>.</li>
                                                <li>Kwenye kisanduku cha <b>Value</b>, hakikisha pameandikwa ile kodi ndefu (inayoanza na <b>AIza...</b>).</li>
                                                <li><b>MUHIMU:</b> Usiandike jina kama "GEMINI_API_KEY" kwenye Value. Futa neno hilo uweke kodi yenyewe.</li>
                                                <li>Bonyeza kitufe cha bluu cha <b>Apply Changes</b> kule chini kabisa ili mfumo uisome.</li>
                                            </ol>
                                        </div>
                                    )}
                                </div>
                            )}

                            {audioData && (
                                <div className="bg-slate-900/80 p-6 rounded-2xl border border-slate-700 space-y-6 animate-in slide-in-from-bottom-2">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sky-400 font-black uppercase text-xs tracking-tighter">Generated Result</h3>
                                        <button
                                            onClick={handleSaveSpeech}
                                            disabled={isSpeechSaved}
                                            className={`flex items-center gap-2 py-2 px-4 rounded-full text-xs font-bold transition-all ${
                                                isSpeechSaved ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                            }`}
                                        >
                                            {isSpeechSaved ? <><Check className="w-4 h-4" /> Saved</> : <><Bookmark className="w-4 h-4" /> Save to Library</>}
                                        </button>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-center gap-6">
                                        <button 
                                            onClick={handlePlayPause}
                                            className="w-20 h-20 bg-sky-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform active:scale-95 shadow-xl shadow-sky-900/40"
                                        >
                                            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                                        </button>
                                        <div className="flex-1 w-full grid grid-cols-2 gap-3">
                                            <button onClick={handleDownloadWav} className="flex items-center justify-center gap-2 bg-slate-800 p-3 rounded-xl border border-slate-700 hover:bg-slate-700 transition-colors font-bold text-sm">
                                                <Download className="w-4 h-4" /> WAV
                                            </button>
                                            <button 
                                                onClick={handleDownloadMp3} disabled={isEncodingMp3}
                                                className="flex items-center justify-center gap-2 bg-slate-800 p-3 rounded-xl border border-slate-700 hover:bg-slate-700 disabled:opacity-50 transition-colors font-bold text-sm"
                                            >
                                                {isEncodingMp3 ? <Spinner /> : <Download className="w-4 h-4" />} MP3
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Transcribe Mode */}
                    {mode === 'transcribe' && (
                        <div className="space-y-6 animate-fade-in relative min-h-[300px]">
                            <div 
                                className={`transition-all ${!isVIP ? 'opacity-30 cursor-pointer' : ''}`}
                                onClick={() => {
                                    if (!isVIP) {
                                        setVipAlert("Huduma ya 'Transcribe' ni kwa wateja wa VIP tu. Panda kifurushi sasa!");
                                        setTimeout(() => setVipAlert(null), 5000);
                                    }
                                }}
                            >
                                <div className={`group border-2 border-dashed border-slate-700 rounded-3xl p-12 text-center bg-slate-900/40 hover:bg-slate-900/60 transition-all ${!isVIP ? 'hover:border-amber-500/30' : 'hover:border-sky-500/50'}`}>
                                    <input
                                        type="file" accept="audio/*" onChange={handleFileChange}
                                        disabled={!isVIP}
                                        className={`absolute inset-0 w-full h-full opacity-0 ${isVIP ? 'cursor-pointer z-10' : 'pointer-events-none'}`}
                                    />
                                    <div className="flex flex-col items-center gap-4">
                                        <div className={`p-6 rounded-2xl transition-all ${!isVIP ? 'bg-amber-500/10 text-amber-500' : 'bg-slate-800 text-sky-400 group-hover:scale-105'}`}>
                                            {!isVIP ? <ShieldCheck className="w-12 h-12" /> : <Upload className="w-12 h-12" />}
                                        </div>
                                        <div>
                                            <p className="text-slate-200 font-black text-lg uppercase tracking-wider">
                                                {!isVIP ? "Transcribe (VIP ONLY)" : (transcriptionFile ? transcriptionFile.name : "Choose an audio file")}
                                            </p>
                                            <p className="text-slate-500 text-sm font-medium mt-1">
                                                {!isVIP ? "Huduma hii inapatikana kwa VIP tu." : "MP3, WAV, AAC (Max 20MB)"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleTranscribe}
                                disabled={isTranscribing || !transcriptionFile}
                                className="w-full flex items-center justify-center gap-3 bg-sky-600 text-white font-black py-4 px-6 rounded-2xl hover:bg-sky-500 disabled:bg-slate-700 disabled:text-slate-500 transition-all duration-300 shadow-lg active:scale-[0.98]"
                            >
                                {isTranscribing ? <><Spinner /> ANALYZING...</> : "START TRANSCRIPTION"}
                            </button>

                            {transcriptionError && <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-xl text-red-400 text-sm text-center">{transcriptionError}</div>}

                            {transcriptionResult && (
                                <div className="space-y-4 animate-in slide-in-from-bottom-2">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sky-400 font-black uppercase text-xs tracking-tighter">Transcript</h3>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleCopyText(transcriptionResult)} className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 text-slate-400" title="Copy to clipboard">
                                                <Clipboard className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={handleSaveTranscription} disabled={isSaved}
                                                className={`flex items-center gap-2 py-2 px-4 rounded-full text-xs font-bold transition-all ${
                                                    isSaved ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                                }`}
                                            >
                                                {isSaved ? <><Check className="w-5 h-5" /> Saved</> : <><Bookmark className="w-5 h-5" /> Save to Library</>}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="w-full p-6 bg-slate-900/80 border border-slate-700 rounded-2xl text-slate-200 text-lg leading-relaxed shadow-inner max-h-[400px] overflow-y-auto whitespace-pre-wrap">
                                        {transcriptionResult}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Unified History Library Mode */}
                    {mode === 'history' && (
                        <div className="space-y-6 animate-fade-in">
                            {/* Search & Filter Bar */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="relative flex-1">
                                    <input 
                                        type="text"
                                        placeholder="Search history..."
                                        value={historySearch}
                                        onChange={(e) => setHistorySearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-900/70 border border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none text-slate-200 placeholder-slate-600 transition-all"
                                    />
                                    <div className="absolute left-3 top-3.5 text-slate-600">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                            <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="flex bg-slate-900/70 p-1 border border-slate-700 rounded-xl">
                                    <button 
                                        onClick={() => setHistoryTypeFilter('all')}
                                        className={`px-4 py-2 rounded-lg text-xs font-black transition-all ${historyTypeFilter === 'all' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                                    >ALL</button>
                                    <button 
                                        onClick={() => setHistoryTypeFilter('tts')}
                                        className={`px-4 py-2 rounded-lg text-xs font-black transition-all ${historyTypeFilter === 'tts' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                                    >TTS</button>
                                    <button 
                                        onClick={() => setHistoryTypeFilter('transcribe')}
                                        className={`px-4 py-2 rounded-lg text-xs font-black transition-all ${historyTypeFilter === 'transcribe' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                                    >VOICE</button>
                                </div>
                            </div>

                            {/* History Items */}
                            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700">
                                {filteredHistory.length > 0 ? (
                                    filteredHistory.map((item) => (
                                        <div key={item.id} className="group bg-slate-900/40 border border-slate-700/50 hover:border-slate-600 rounded-2xl p-5 transition-all hover:bg-slate-800/40">
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${item.type === 'tts' ? 'bg-sky-500/20 text-sky-400' : 'bg-purple-500/20 text-purple-400'}`}>
                                                            {item.type.toUpperCase()}
                                                        </span>
                                                        <h4 className="text-sm font-bold text-slate-200 truncate max-w-[200px] sm:max-w-md">{item.title}</h4>
                                                        {item.type === 'tts' && (item as SavedSpeech).seed !== undefined && (
                                                            <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full border border-slate-700">
                                                                Seed: {(item as SavedSpeech).seed}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-[10px] text-slate-600 font-mono">{item.date}</span>
                                                </div>
                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button 
                                                        onClick={() => item.type === 'tts' ? handleRestoreSpeech(item as SavedSpeech) : handleRestoreTranscription(item as SavedTranscription)}
                                                        className="p-2 hover:bg-sky-500/20 hover:text-sky-400 text-slate-500 rounded-lg transition-colors"
                                                        title="Retrieve / Open in Editor"
                                                    >
                                                        <ChevronUp className="w-5 h-5" />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleCopyText(item.text)}
                                                        className="p-2 hover:bg-slate-700 text-slate-500 rounded-lg transition-colors"
                                                        title="Copy Text"
                                                    >
                                                        <Clipboard className="w-5 h-5" />
                                                    </button>
                                                    <button 
                                                        onClick={() => item.type === 'tts' ? handleDeleteSpeech(item.id) : handleDeleteTranscription(item.id)}
                                                        className="p-2 hover:bg-red-500/20 hover:text-red-400 text-slate-500 rounded-lg transition-colors"
                                                        title="Delete permanently"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="relative">
                                                <p className="text-sm text-slate-400 line-clamp-3 leading-relaxed whitespace-pre-wrap">
                                                    {item.text}
                                                </p>
                                                <div className="absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
                                        <div className="p-6 bg-slate-900/50 rounded-full text-slate-700">
                                            <Clock className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className="text-slate-400 font-bold">No records found</p>
                                            <p className="text-slate-600 text-sm">Save your creations to see them here.</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {filteredHistory.length > 0 && (
                                <div className="pt-6 border-t border-slate-700 flex justify-end">
                                    <button 
                                        onClick={handleClearAllHistory}
                                        className="text-xs font-black text-red-500/70 hover:text-red-400 transition-colors flex items-center gap-2 uppercase tracking-tighter"
                                    >
                                        <Trash2 className="w-4 h-4" /> Wipe Library
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Admin Mode */}
                    {mode === 'admin' && isOwner && <AdminPortal />}
                </div>
            </div>

            {/* Top-up Modal */}
            {showTopUp && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="w-full max-w-md bg-slate-900 border border-slate-700/50 rounded-3xl p-8 shadow-[0_0_50px_rgba(14,165,233,0.15)] space-y-6 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-sky-500 to-transparent opacity-50"></div>
                        
                        <div className="absolute top-0 right-0 p-4">
                            <button onClick={() => setShowTopUp(false)} className="text-slate-500 hover:text-slate-300 transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        
                        <div className="text-center space-y-3">
                            <div className="w-16 h-16 bg-sky-500/10 text-sky-400 rounded-2xl flex items-center justify-center mx-auto rotate-3 group-hover:rotate-0 transition-transform">
                                <Wallet className="w-8 h-8" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-white uppercase tracking-tighter">ROM AUDIO LAB STUDIO</h2>
                                <p className="text-sky-500/60 text-[10px] uppercase font-bold tracking-[0.3em]">CREDIT MANAGEMENT</p>
                            </div>
                            <p className="text-slate-400 text-sm">Chagua kifurushi cha kuanza kutengeneza sauti leo.</p>
                        </div>

                        <div className="space-y-3">
                            {/* Serengeti Pack */}
                            <div 
                                onClick={() => {
                                    const msg = encodeURIComponent("Habari ROM MEDIA, nahitaji Serengeti Pack (TZS 5,000) kwa ajili ya ROM AUDIO LAB.");
                                    window.open(`https://wa.me/255784000082?text=${msg}`, '_blank');
                                }}
                                className="p-4 bg-slate-800/40 border border-slate-700 rounded-2xl flex items-center justify-between group hover:border-sky-500/50 transition-all hover:bg-slate-800 cursor-pointer"
                            >
                                <div>
                                    <p className="font-black text-white uppercase text-xs tracking-wider">Serengeti Pack</p>
                                    <p className="text-[10px] text-slate-500 font-mono">VIP Access (Masaa 48 - 50 Credit)</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-black text-sky-400 text-sm">TZS 5,000</p>
                                    <button className="text-[9px] bg-sky-600/20 text-sky-400 group-hover:bg-sky-600 group-hover:text-white px-3 py-1 rounded-full mt-1 font-bold transition-all border border-sky-500/30">NUNUA</button>
                                </div>
                            </div>
                            
                            {/* Kilimanjaro Pack */}
                            <div 
                                onClick={() => {
                                    const msg = encodeURIComponent("Habari ROM MEDIA, nahitaji Kilimanjaro Pack (TZS 15,000) kwa ajili ya ROM AUDIO LAB.");
                                    window.open(`https://wa.me/255784000082?text=${msg}`, '_blank');
                                }}
                                className="p-4 bg-slate-800/80 border-2 border-sky-500 rounded-2xl flex items-center justify-between relative overflow-hidden group hover:scale-[1.02] transition-all cursor-pointer"
                            >
                                <div className="absolute top-0 right-0 bg-sky-500 text-[8px] font-black text-white px-2 py-0.5 rounded-bl-lg uppercase tracking-tighter">MOST POPULAR</div>
                                <div>
                                    <p className="font-black text-white uppercase text-xs tracking-wider">Kilimanjaro Pack</p>
                                    <p className="text-[10px] text-sky-400/80 font-mono">Premium VIP (Unlimited Access - 150 Credit)</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-black text-sky-400 text-sm">TZS 15,000</p>
                                    <button className="text-[9px] bg-sky-600 text-white px-4 py-1.5 rounded-full mt-1 font-black shadow-lg shadow-sky-500/20">NUNUA SASA</button>
                                </div>
                            </div>

                            {/* Quick Pack */}
                            <div 
                                onClick={() => {
                                    const msg = encodeURIComponent("Habari ROM MEDIA, nahitaji Quick Pack (TZS 1,000) kwa ajili ya ROM AUDIO LAB.");
                                    window.open(`https://wa.me/255784000082?text=${msg}`, '_blank');
                                }}
                                className="p-4 bg-slate-800/40 border border-slate-700 rounded-2xl flex items-center justify-between group hover:border-sky-500/50 transition-all hover:bg-slate-800 cursor-pointer"
                            >
                                <div>
                                    <p className="font-black text-white uppercase text-xs tracking-wider">Quick Pack</p>
                                    <p className="text-[10px] text-slate-500 font-mono">500 Characters (10 Credit)</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-black text-sky-400 text-sm">TZS 1,000</p>
                                    <button className="text-[9px] bg-sky-600/20 text-sky-400 group-hover:bg-sky-600 group-hover:text-white px-3 py-1 rounded-full mt-1 font-bold transition-all border border-sky-500/30">NUNUA</button>
                                </div>
                            </div>

                            {/* Daily Pack */}
                            <div 
                                onClick={() => {
                                    const msg = encodeURIComponent("Habari ROM MEDIA, nahitaji Daily Pack (TZS 3,000) kwa ajili ya ROM AUDIO LAB.");
                                    window.open(`https://wa.me/255784000082?text=${msg}`, '_blank');
                                }}
                                className="p-4 bg-slate-800/40 border border-slate-700 rounded-2xl flex items-center justify-between group hover:border-emerald-500/50 transition-all hover:bg-slate-800 cursor-pointer"
                            >
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="font-black text-white uppercase text-xs tracking-wider">Daily Pack</p>
                                        <span className="text-[8px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded font-black">NEW</span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 font-mono">1500 Characters (Masaa 24 - 30 Credit)</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-black text-emerald-400 text-sm">TZS 3,000</p>
                                    <button className="text-[9px] bg-emerald-600/20 text-emerald-400 group-hover:bg-emerald-600 group-hover:text-white px-3 py-1 rounded-full mt-1 font-bold transition-all border border-emerald-500/30">NUNUA</button>
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-950/50 border border-slate-800 p-4 rounded-2xl space-y-2">
                            <div className="flex items-start gap-3">
                                <div className="p-1 bg-sky-500/10 rounded-lg shrink-0 mt-0.5">
                                    <AlertCircle className="w-3.5 h-3.5 text-sky-400" />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[10px] text-slate-300 font-bold uppercase tracking-widest">Viwango vya Huduma (Tiers)</p>
                                    <div className="grid grid-cols-2 gap-2 text-[9px] text-slate-500 mt-1">
                                        <div className="p-1 px-2 border border-slate-800 rounded bg-slate-900/50">
                                            <span className="text-slate-400 block font-bold">FREE</span>
                                            50 Chars + Watermark
                                        </div>
                                        <div className="p-1 px-2 border border-slate-800 rounded bg-slate-900/50">
                                            <span className="text-sky-400 block font-bold">STANDARD (Tsh 1,000)</span>
                                            500 Chars + 2 Voices
                                        </div>
                                        <div className="p-1 px-2 border border-slate-800 rounded bg-slate-900/50">
                                            <span className="text-amber-400 block font-bold">PREMIUM (Tsh 3,000)</span>
                                            1,500 Chars + 4 Voices
                                        </div>
                                        <div className="p-1 px-2 border border-slate-800 rounded bg-slate-900/50">
                                            <span className="text-emerald-400 block font-bold">VIP (Tsh 5,000+)</span>
                                            All Voices + Seed + Transcribe
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-slate-500 leading-snug mt-2 pt-2 border-t border-slate-800/50">
                                        Vifurushi vinakuongezea salio kulingana na mahitaji yako. Tuma ujumbe sasa kuongezwa kwenye **Standard** au **Premium**.
                                    </p>
                                </div>
                             </div>
                            <div className="flex items-center gap-3 pt-2 border-t border-slate-800/50">
                                <div className="flex -space-x-2">
                                    <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[8px] font-bold text-slate-300">MP</div>
                                    <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[8px] font-bold text-slate-300">TP</div>
                                    <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[8px] font-bold text-slate-300">AM</div>
                                </div>
                                <p className="text-[10px] text-slate-500 leading-snug flex-1">
                                    Tuma ujumbe WhatsApp kwa <strong>0784 000 082</strong> ili kuongezewa salio.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            {Footer}
        </div>
    );
};

export default App;
